import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useI18n } from "@/i18n";
import { getArtworks, getStorefrontImages, type Artwork } from "@/lib/api";
import ArtworkCard from "@/components/ArtworkCard";
import { useCart } from "@/lib/cart";
import { FALLBACK_ARTWORKS } from "@/lib/fallbackArtworks";
import { formatPrice, getArtworkPreviewImage, getArtworkTitle } from "@/lib/utils";

type Category = "print" | "original";

const CATEGORY_IMAGES: Record<Category | "all", string[]> = {
  all: ["/images/hero-artwork.jpg"],
  print: ["/images/print-category.jpg"],
  original: ["/images/original-category.jpg"],
};

const CATEGORY_IMAGE_SETTINGS: Record<Category | "all", "shop_all_cover_image" | "print_category_image" | "original_category_image"> = {
  all: "shop_all_cover_image",
  print: "print_category_image",
  original: "original_category_image",
};

export default function Gallery() {
  const { category } = useParams<{ category?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t, locale } = useI18n();
  const [artworks, setArtworks] = useState<Artwork[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [storefrontImages, setStorefrontImages] = useState<Record<string, string>>({});
  const { addItem } = useCart();

  const activeCategory = category as Category | undefined;
  const hideSold = searchParams.get("hide_sold") === "true";
  const heroKey = activeCategory || "all";

  useEffect(() => {
    setLoading(true);
    getArtworks({
      category: activeCategory,
      status: hideSold ? "available" : "all",
      limit: 50,
    })
      .then((res) => {
        setArtworks(res.artworks);
        setTotal(res.total);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [activeCategory, hideSold]);

  useEffect(() => {
    getStorefrontImages().then(setStorefrontImages).catch(() => undefined);
  }, []);

  const categories: { key: Category | "all"; label: string }[] = [
    { key: "all", label: t.nav.shopAll },
    { key: "print", label: t.categories.print },
    { key: "original", label: t.categories.original },
  ];

  const pageTitle = activeCategory ? t.categories[activeCategory] : t.nav.shopAll;
  const description = activeCategory
    ? t.categories[`${activeCategory}Desc`]
    : locale === "it"
      ? "Una selezione di opere originali e stampe d'arte per spazi vissuti."
      : "A considered selection of original works and art prints for lived-in spaces.";
  const fallbackWorks = FALLBACK_ARTWORKS.filter((work) => !activeCategory || work.category === activeCategory);
  const heroImages = storefrontImages[CATEGORY_IMAGE_SETTINGS[heroKey]] ? [storefrontImages[CATEGORY_IMAGE_SETTINGS[heroKey]]] : CATEGORY_IMAGES[heroKey];

  return (
    <div className="gallery-page">
      <section className="gallery-editorial-hero is-category">
        <div className="gallery-hero-copy">
          <span className="gallery-index">Dalila Di Maria · Collection 01</span>
          <h1>{pageTitle}</h1>
          <p>{description}</p>
          <span className="gallery-scroll-note">
            {locale === "it" ? "Scopri la collezione" : "Discover the collection"}
          </span>
        </div>
        <div className="gallery-hero-image-wrap">
          {heroImages.map((image, index) => (
            <div className="gallery-hero-image-panel" key={image}>
              <img src={image} alt={`${pageTitle} ${index + 1}`} className="gallery-hero-image" />
            </div>
          ))}
          <span className="gallery-image-caption">{pageTitle} · Dalila Di Maria</span>
        </div>
      </section>

      <section className="gallery-catalogue container">
        <div className="gallery-toolbar">
          <nav className="gallery-tabs" aria-label="Shop categories">
            {categories.map((item) => {
              const isActive = (item.key === "all" && !activeCategory) || item.key === activeCategory;
              return (
                <Link
                  key={item.key}
                  to={item.key === "all" ? "/gallery" : `/gallery/${item.key}`}
                  className={isActive ? "active" : ""}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <label className="gallery-sold-toggle">
            <input
              type="checkbox"
              checked={hideSold}
              onChange={(event) => {
                const next = new URLSearchParams(searchParams);
                // Sold work is visible by default. The filter only hides it on request.
                next.delete("sold");
                if (event.target.checked) next.set("hide_sold", "true");
                else next.delete("hide_sold");
                setSearchParams(next);
              }}
            />
            <span>{locale === "it" ? "Nascondi venduti" : "Hide sold"}</span>
          </label>
        </div>

        {loading ? (
          <div className="gallery-artwork-grid">
            {Array.from({ length: 6 }).map((_, index) => (
              <div key={index} className="gallery-skeleton">
                <div />
                <span />
              </div>
            ))}
          </div>
        ) : artworks.length === 0 ? (
          <div className="gallery-artwork-grid gallery-preview-grid">
            {fallbackWorks.map((work, index) => {
              const title = getArtworkTitle(work, locale);

              return (
                <article className="collection-preview-card" key={work.slug}>
                  <Link to={`/artworks/${work.slug}`} className="collection-preview-image" aria-label={title}>
                    <img src={getArtworkPreviewImage(work)} alt={title} />
                    <span>{String(index + 1).padStart(2, "0")}</span>
                  </Link>
                  <div className="collection-preview-meta">
                    <Link to={`/artworks/${work.slug}`}>
                      <h2>{title}</h2>
                      <p>{t.categories[work.category]}</p>
                    </Link>
                    <div className="collection-preview-buy">
                      <strong>{formatPrice(work.price, work.currency)}</strong>
                      {work.category === "print" ? (
                        <Link to={`/artworks/${work.slug}`}>{t.artwork.addToCart}</Link>
                      ) : <button
                        onClick={() => addItem({
                          id: work.id,
                          slug: work.slug,
                          title,
                          category: work.category,
                          image: getArtworkPreviewImage(work),
                          price: work.price,
                          currency: work.currency,
                          stockQuantity: work.stock_quantity,
                        })}
                      >
                        {t.artwork.addToCart}
                      </button>}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <>
            <div className="gallery-count">{total} {locale === "it" ? "opere" : "works"}</div>
            <div className="gallery-artwork-grid">
              {artworks.map((artwork) => <ArtworkCard key={artwork.id} artwork={artwork} />)}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
